import React, { useState, useEffect } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import SvgYellowExclamation from '@components/ringil3/icons/YellowExclamation.tsx';
import RichTextEditor from '@components/RingilComponents/RichTextEditor/RichTextEditor.tsx';
import CollapsibleNote from '@components/RingilComponents/RichTextEditor/CollapsibleNote.tsx';
import { NOTE_SOFT_LIMIT } from '@components/RingilComponents/RichTextEditor/constants.ts';
import clsx from 'clsx';

interface ComponentProps {
  secured?: SecuredConfig;
  value?: string;
  onChange: (carrierNote: string) => void;
  readOnly?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  errorMessage?: string;
  dataTestId?: string;
  dataTestIdInput?: string;
  dataTestIdSave?: string;
  dataTestIdClose?: string;
}

const CarrierNoteTag: React.FC<ComponentProps> = ({
  secured,
  value,
  onChange,
  readOnly = false,
  required = false,
  fullWidth = false,
  errorMessage,
  dataTestId,
  dataTestIdInput,
  dataTestIdSave,
  dataTestIdClose,
}) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  return (
    <>
      <ButtonTag
        secured={secured}
        label={tr(`CarrierNoteTag.title`, `Note for carrier`)}
        // Pass the rendered note HTML as the display node when present, but keep '' when empty so ButtonTag's
        // empty/required variant logic (which checks value === '') still works.
        value={value ? <CollapsibleNote html={value} /> : ''}
        icon={<SvgYellowExclamation />}
        onSave={success => {
          onChange(internalValue);
          success();
        }}
        autoClose
        readonly={readOnly}
        required={required}
        errorMessage={errorMessage}
        popupWidth={'527px'}
        alignTop
        className={clsx('max-w-[750px]', fullWidth && 'w-full')}
        dataTestId={dataTestId}
        dataTestIdSave={dataTestIdSave}
        dataTestIdClose={dataTestIdClose}
      >
        <RichTextEditor value={internalValue} onChange={setInternalValue} maxLength={NOTE_SOFT_LIMIT} allowCodeBlock={false} autoFocus dataTestId={dataTestIdInput} />
      </ButtonTag>
    </>
  );
};

export default CarrierNoteTag;
